/**
 * Color palettes and utilities for the Spinner Wheel
 */

import type { WheelEntry, WheelSegment } from '../types/wheel';

// Predefined color schemes for different segment counts
export const SEGMENT_COLORS: Record<number, string[]> = {
  1: ['#BC2828'],
  2: ['#D3362A', '#199BD3'],
  3: ['#115FE3', '#D3362A', '#FED103'],
  4: ['#8C01FA', '#FB6D02', '#FEE603', '#62C52F'],
  5: ['#8C01FA', '#D50156', '#FC8E02', '#62C52F', '#115FE3'],
  6: ['#D50156', '#ED4F24', '#FDC702', '#62C52F', '#1581DA', '#5D0AF9'],
  7: ['#C4017D', '#ED4F24', '#FDBA02', '#CFDF02', '#62C52F', '#0A2FF1', '#7E04FA'],
  8: ['#8D01F8', '#CA016F', '#FB3501', '#FC9E02', '#FEE503', '#62C52F', '#0760ED', '#2C13F8'],
  9: ['#D10160', '#FB2D01', '#FF6700', '#FED103', '#B0DA02', '#31AF64', '#0F57E5', '#460EF8', '#9001F1'],
  10: [
    '#D3015A',
    '#FB2C01',
    '#FC7C02',
    '#FED103',
    '#CFDF02',
    '#68C822',
    '#22A0BE',
    '#0719F7',
    '#5D0AF9',
    '#9701E0',
  ],
  11: [
    '#D60153',
    '#FB2B01',
    '#FC7F02',
    '#FDC402',
    '#F0E503',
    '#77D100',
    '#39AD8C',
    '#105DE4',
    '#2A13F8',
    '#6E07F9',
    '#9B01D7',
  ],
  12: [
    '#A701BD',
    '#DB0149',
    '#FA2601',
    '#FB6A02',
    '#FCAB02',
    '#FEDD03',
    '#B6DB02',
    '#61C531',
    '#25A2B7',
    '#0E4BE9',
    '#3B10F8',
    '#8203FA',
  ],
};

// Hex colors for confetti
export const CONFETTI_COLORS = ['#4EAAF1', '#FFAF09', '#28C270', '#FF5DA1', '#7669BF', '#124345'];

// Predefined color themes/gradients
export const COLOR_THEMES: Record<string, string[]> = {
  // Default rainbow theme
  rainbow: [],  // Empty means use default behavior (HSL generation)

  // Warm sunset gradient
  sunset: ['#FF6B6B', '#FF8E53', '#FFBD59', '#FFE066', '#FFA94D', '#FF7043', '#FF5722', '#E64A19'],

  // Cool ocean gradient
  ocean: ['#0077B6', '#00B4D8', '#48CAE4', '#90E0EF', '#00A896', '#02C39A', '#028090', '#05668D'],

  // Forest/nature gradient
  forest: ['#2D6A4F', '#40916C', '#52B788', '#74C69D', '#95D5B2', '#B7E4C7', '#1B4332', '#081C15'],

  // Purple/pink gradient
  candy: ['#9D4EDD', '#C77DFF', '#E0AAFF', '#FF6B9D', '#FF85A1', '#FFA5B4', '#7B2CBF', '#5A189A'],

  // Neon/vibrant
  neon: ['#FF00FF', '#00FFFF', '#FFFF00', '#FF00AA', '#00FF00', '#FF6600', '#0066FF', '#FF0066'],

  // Pastel colors
  pastel: ['#FFB5BA', '#FFDAC1', '#FFFFD1', '#B5EAD7', '#C7CEEA', '#E2B6CF', '#F0E6EF', '#D4F0F0'],

  // Monochrome blue
  blues: ['#03045E', '#023E8A', '#0077B6', '#0096C7', '#00B4D8', '#48CAE4', '#90E0EF', '#ADE8F4'],

  // Monochrome red
  reds: ['#590D22', '#800F2F', '#A4133C', '#C9184A', '#FF4D6D', '#FF758F', '#FF8FA3', '#FFB3C1'],

  // Monochrome green
  greens: ['#004B23', '#006400', '#007200', '#008000', '#38B000', '#70E000', '#9EF01A', '#CCFF33'],

  // Earth tones
  earth: ['#6B4423', '#8B5A2B', '#A0522D', '#CD853F', '#DEB887', '#D2691E', '#8B4513', '#A52A2A'],

  // Retro 80s
  retro: ['#FF1493', '#00CED1', '#FFD700', '#FF69B4', '#00BFFF', '#FF6347', '#9370DB', '#32CD32'],
};

/**
 * Get colors for a theme, generating them if needed
 */
export function getThemeColors(theme: string, count: number): string[] {
  const themeColors = COLOR_THEMES[theme];

  // If theme doesn't exist or is empty (rainbow), generate HSL colors
  if (!themeColors || themeColors.length === 0) {
    return Array(count).fill(0).map((_, i) => {
      const hue = (i / count) * 360;
      return `hsla(${hue}, 80%, 50%, 1)`;
    });
  }

  // If we have enough colors, use them directly (cycling if needed)
  return Array(count).fill(0).map((_, i) => themeColors[i % themeColors.length]);
}

// Default entries for the wheel
export const DEFAULT_ENTRIES: WheelEntry[] = [
  { name: 'Entry 1', id: '1' },
  { name: 'Entry 2', id: '2' },
  { name: 'Entry 3', id: '3' },
  { name: 'Entry 4', id: '4' },
  { name: 'Entry 5', id: '5' },
  { name: 'Entry 6', id: '6' },
  { name: 'Entry 7', id: '7' },
  { name: 'Entry 8', id: '8' },
];

/**
 * Get color for a segment based on index and total count
 */
export function getSegmentColor(index: number, total: number, customColors?: string[]): string {
  // Use custom colors if provided
  if (customColors && customColors[index]) {
    return customColors[index];
  }

  // Use predefined colors if available
  if (SEGMENT_COLORS[total]) {
    return SEGMENT_COLORS[total][index];
  }

  // Generate HSL color for larger segment counts
  const hue = (index / total) * 360;
  return `hsla(${hue}, 80%, 50%, 1)`;
}

/**
 * Measure text width using canvas context
 */
let measureCanvas: HTMLCanvasElement | null = null;
let measureContext: CanvasRenderingContext2D | null = null;

function getTextWidth(text: string, font: string): number {
  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
    measureContext = measureCanvas.getContext('2d');
  }
  if (!measureContext) return text.length * 10; // fallback
  measureContext.font = font;
  return measureContext.measureText(text).width;
}

/**
 * Truncate text to fit within specified pixel width
 * Uses binary search for efficiency (like the original implementation)
 */
export function truncateText(text: string, maxWidth: number, fontSize: number = 26): string {
  if (!text) return '';

  const font = `bold ${fontSize}px Arial`;
  const textWidth = getTextWidth(text, font);

  // If text fits, return as-is
  if (textWidth <= maxWidth) return text;

  // Binary search to find optimal truncation point
  let low = 0;
  let high = text.length;

  while (high - low > 1) {
    const mid = Math.floor((low + high) / 2);
    const truncated = text.substring(0, mid) + '...';
    const width = getTextWidth(truncated, font);

    if (width <= maxWidth) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return text.substring(0, low) + '...';
}

// Max segments before switching to rainbow mode (no text, no visible lines)
const MAX_SEGMENTS_WITH_TEXT = 60;

/**
 * Generate segments config from entries
 * When there are more than 60 entries, creates 60 rainbow segments without text
 */
export function generateSegments(
  entries: WheelEntry[],
  maxTextWidth: number = 121,
  fontSize: number = 26,
  customColors?: string[]
): WheelSegment[] {
  const total = entries.length;

  // When > 60 entries: create 60 segments with no text
  // strokeStyle matches fillStyle so lines are invisible
  if (total > MAX_SEGMENTS_WITH_TEXT) {
    return Array(MAX_SEGMENTS_WITH_TEXT)
      .fill(0)
      .map((_, index) => {
        // Use custom colors if provided, otherwise generate HSL rainbow
        let color: string;
        if (customColors && customColors.length > 0) {
          color = customColors[index % customColors.length];
        } else {
          const hue = (index / MAX_SEGMENTS_WITH_TEXT) * 360;
          color = `hsla(${hue}, 80%, 50%, 1)`;
        }
        return {
          fillStyle: color,
          strokeStyle: color, // Same as fill - makes lines invisible
          text: '', // No text
          fullText: '',
        };
      });
  }

  // Normal mode: show text with predefined or generated colors
  return entries.map((entry, index) => {
    const color = getSegmentColor(index, total, customColors);
    return {
      fillStyle: color,
      text: truncateText(entry.name, maxTextWidth, fontSize),
      fullText: entry.name,
    };
  });
}
